import type { ReactNode } from "react";

// Dashed-ring "+ add" affordance. Visual primitive only — no form
// expansion, no state. The dashed ring signals "drop zone / placeholder
// slot here" — distinct from <PlusButton>, which is just inline text.
//
// `fullWidth` switches between the two documented scales:
//   false (default) — inline pill (px-3 py-2 text-smycles-meta). For
//     "create another card-shaped item" inline in a list — folder rows,
//     inline add-affordances.
//   true            — full-width placeholder box (px-4 py-4 + the
//     uppercase + tracked label). For empty-section placeholders where
//     the chrome dominates the empty space. Used internally by
//     <AddForm variant="empty">.
//
// Chrome: solid 15% ink ring. Hover lifts the ring to accent/50 and the
// text to accent. (An earlier iteration used `ring-dashed`, which isn't
// a real Tailwind utility — silently fell back to solid for months.
// Solid is what shipped; solid is what we keep.)
//
// `glyph` defaults to "+" — override sparingly.

interface AddPlaceholderProps {
  id?: string;
  label: ReactNode;
  onClick: () => void;
  glyph?: ReactNode;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  title?: string;
}

export function AddPlaceholder({
  label,
  onClick,
  glyph = "+",
  fullWidth = false,
  disabled = false,
  className = "",
  title,
  id,
}: AddPlaceholderProps) {
  const layout = fullWidth
    ? "flex w-full items-center justify-center gap-2 px-4 py-4"
    : "inline-flex items-center gap-2 px-3 py-2 text-smycles-meta";

  const labelEl = fullWidth ? (
    <span className="text-smycles-label tracking-[0.15em]">{label}</span>
  ) : (
    label
  );

  const glyphEl = fullWidth ? (
    <span className="text-smycles-title leading-none">{glyph}</span>
  ) : (
    <span>{glyph}</span>
  );

  return (
    <button
      id={id}
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${layout} rounded-md ring-1 ring-ink/15 text-ink-muted transition hover:text-ink-accent hover:ring-accent-decoration/50 disabled:opacity-40 ${className}`}
    >
      {glyphEl}
      {labelEl}
    </button>
  );
}
